import {
    findChainSuccessor,
    findOverlapFields,
    isLastDatedPriceInChain,
    previousDay,
    shiftUtcDayISO,
    toDayKey,
    toUtcMidnightISO,
    validateSingleOpenEnded,
} from "components/Title/PriceHistory/utils";
import type { TFormCreateTitle } from "components/TitleFormContainer/types";
import type { CountryCode } from "countries";
import { removeVatFromPrice } from "helpers/utils";
import type { UseFormClearErrors, UseFormGetValues, UseFormReturn, UseFormSetError } from "react-hook-form";
import type { TFunction } from "react-i18next";
import { createAppAsyncThunk } from "store/appAsyncThunk";
import type { CreatePriceHistoryEntryParams, IPriceHistoryEntry, IPriceHistoryFormValues } from "store/priceHistory";
import {
    selectHasDateGapError,
    selectHasNoActiveEntryError,
    selectHasNoFutureEntryError,
} from "store/priceHistory/priceHistory.selectors";
import { addDraftEntry, updateDraftEntry } from "store/priceHistory/priceHistoryStore";

export const getChannelFormKey = (getValues: UseFormGetValues<TFormCreateTitle>, channelId?: number) => {
    const currentChannel = getValues().overriddenChannelsData?.findIndex((c) => c.channelId === channelId);
    return currentChannel >= 0
        ? (`overriddenChannelsData.${currentChannel}.priceHistory` as const)
        : (`defaultPriceHistory` as const);
};

export const setPriceHistoryErrors = createAppAsyncThunk<
    void,
    {
        channelId?: number;
        getValues: UseFormGetValues<TFormCreateTitle>;
        setError: UseFormSetError<TFormCreateTitle>;
        clearErrors: UseFormClearErrors<TFormCreateTitle>;
    }
>("priceHistory/setPriceHistoryErrors", async ({ getValues, channelId, clearErrors, setError }, { getState }) => {
    const hasNoActiveEntryError = selectHasNoActiveEntryError(channelId)(getState());
    const hasDateGapError = selectHasDateGapError(channelId)(getState());
    const hasNoFutureEntryError = selectHasNoFutureEntryError(channelId)(getState());
    const errorKey = getChannelFormKey(getValues, channelId);

    if (hasNoActiveEntryError) {
        setError(errorKey, { type: "noActivePriceHistory" });
    } else if (hasDateGapError) {
        setError(errorKey, { type: "hasDateGapError" });
    } else if (hasNoFutureEntryError) {
        setError(errorKey, { type: "hasNoFutureEntryError" });
    } else {
        clearErrors(errorKey);
    }
});

export const priceHistorySubmit = createAppAsyncThunk<
    boolean,
    {
        titleId?: number;
        channelId?: number;
        isEditing: boolean;
        data: IPriceHistoryFormValues;
        usesPriceWithVat: boolean;
        parentFormContext: UseFormReturn<TFormCreateTitle, any, undefined>;
        setError: UseFormSetError<IPriceHistoryFormValues>;
        t: TFunction<"translation", undefined>;
        editEntry?: IPriceHistoryEntry | null;
        companyCountryCode: string;
    }
>(
    "priceHistory/priceHistorySubmit",
    async (
        {
            setError,
            data,
            t,
            isEditing,
            channelId,
            usesPriceWithVat,
            titleId,
            editEntry,
            companyCountryCode,
            parentFormContext,
        },
        { dispatch, getState },
    ): Promise<boolean> => {
        const { draftEntries, isDraft } = getState().priceHistory;

        if (!isDraft) return false;

        if (isNaN(parseFloat(data.price))) {
            setError("price", { message: t("NewElement.Number") });
            return false;
        }

        const forceOpenEnded = Boolean(isEditing && editEntry && isLastDatedPriceInChain(draftEntries, editEntry));
        const resolvedEndDate = forceOpenEnded ? null : data.endDate;

        if (data.startDate && resolvedEndDate && toDayKey(resolvedEndDate) < toDayKey(data.startDate)) {
            setError("endDate", { message: t("PriceHistory.EndBeforeStartError") });
            return false;
        }

        const previousOpenEnded =
            !isEditing && data.startDate
                ? draftEntries.find(
                      (e) => e.channelId === channelId && e.currency === data.currency && e.endDate === null,
                  )
                : undefined;

        const autoCloseEndDateISO =
            previousOpenEnded && data.startDate ? toUtcMidnightISO(previousDay(data.startDate)) : null;

        // Editing a period's end ripples to its chain successor: the next period
        // always starts the day after this one ends. A ripple that would swallow
        // the successor entirely (new start past its end) is rejected as overlap.
        const newEndISO = resolvedEndDate ? toUtcMidnightISO(resolvedEndDate) : null;
        const successor = isEditing && editEntry ? findChainSuccessor(draftEntries, editEntry) : undefined;
        const rippledSuccessor =
            successor && newEndISO && (!editEntry?.endDate || toDayKey(newEndISO) !== toDayKey(editEntry.endDate))
                ? { ...successor, startDate: shiftUtcDayISO(newEndISO, 1) }
                : undefined;
        if (rippledSuccessor?.endDate && toDayKey(rippledSuccessor.startDate!) > toDayKey(rippledSuccessor.endDate)) {
            setError("endDate", { message: t("PriceHistory.OverlapError") });
            return false;
        }

        const dataForValidation = { ...data, endDate: resolvedEndDate };
        let entriesForValidation =
            previousOpenEnded && autoCloseEndDateISO
                ? draftEntries.map((e) => (e.id === previousOpenEnded.id ? { ...e, endDate: autoCloseEndDateISO } : e))
                : draftEntries;
        if (rippledSuccessor) {
            entriesForValidation = entriesForValidation.map((e) =>
                e.id === rippledSuccessor.id ? rippledSuccessor : e,
            );
        }

        if (validateSingleOpenEnded(entriesForValidation, dataForValidation, editEntry?.id)) {
            setError("endDate", { message: t("PriceHistory.OpenEndedError") });
            return false;
        }
        const overlap = findOverlapFields(entriesForValidation, dataForValidation, editEntry?.id);
        if (overlap) {
            const message = t("PriceHistory.OverlapError");
            if (overlap.startOverlaps) setError("startDate", { message });
            if (overlap.endOverlaps) setError("endDate", { message });
            return false;
        }

        const getDtoPrice = (price: string, currency: string, countryCode?: CountryCode) => {
            if (usesPriceWithVat && currency === "EUR") {
                return Math.round(removeVatFromPrice(parseFloat(price), countryCode ?? companyCountryCode) * 100);
            }
            return Math.round(parseFloat(price) * 100);
        };
        const payload: CreatePriceHistoryEntryParams = {
            ...data,
            price: getDtoPrice(data.price, data.currency),
            startDate: data.startDate ? toUtcMidnightISO(data.startDate) : null,
            endDate: resolvedEndDate ? toUtcMidnightISO(resolvedEndDate) : null,
            titleId,
            childCurrencies: data.childCurrencies.map((c) => ({
                currency: c.currency,
                country: c.country.code,
                price: getDtoPrice(c.price, c.currency, c.country.code),
            })),
        };

        if (isEditing && editEntry) {
            if (rippledSuccessor) {
                dispatch(updateDraftEntry(rippledSuccessor));
            }
            dispatch(updateDraftEntry({ ...editEntry, ...payload }));
        } else {
            if (previousOpenEnded && autoCloseEndDateISO) {
                dispatch(updateDraftEntry({ ...previousOpenEnded, endDate: autoCloseEndDateISO }));
            }
            dispatch(addDraftEntry(payload));
        }
        dispatch(setPriceHistoryErrors({ ...parentFormContext, channelId }));
        parentFormContext.setValue("shallRecompute", true, { shouldDirty: true });

        return true;
    },
);
